/**
 * Analytics dashboard.
 *
 * Reads the first-party pipeline — no third-party embed, so the numbers here
 * are the real numbers rather than whatever survived an ad blocker.
 */
import { useEffect, useMemo, useState } from 'react';
import { compactNumber, formatDuration, percent } from '@agenz/shared/utils';
import { api } from '../lib/api.js';
import {
  Card,
  EmptyState,
  ErrorNote,
  PageTitle,
  Select,
  Spinner,
} from '../components/ui.jsx';

function Stat({ label, value, sub }) {
  return (
    <Card className="p-5">
      <p className="text-xs font-bold uppercase tracking-widest text-muted">{label}</p>
      <p className="mt-2 font-display text-4xl">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
    </Card>
  );
}

/** Horizontal bars — a full charting library is not worth the bundle here. */
function BarList({ title, rows, valueKey, labelKey, empty }) {
  const max = Math.max(1, ...rows.map((row) => row[valueKey]));

  return (
    <Card className="p-6">
      <h2 className="text-lg">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted">{empty}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((row) => (
            <li key={row[labelKey]}>
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="truncate font-semibold">{row[labelKey]}</span>
                <span className="shrink-0 text-muted">{compactNumber(row[valueKey])}</span>
              </div>
              <div className="mt-1 h-2 bg-line">
                <div
                  className="h-full bg-electric"
                  style={{ width: `${(row[valueKey] / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Heatmap overlay.
 *
 * Points are stored normalised 0–1 against the document, so they can be
 * painted onto a plain aspect box at any size and still land where the visitor
 * actually clicked.
 */
function Heatmap({ paths }) {
  const [path, setPath] = useState(paths[0]?.path ?? '/');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) return;
    setData(null);
    api
      .get(`/api/admin/analytics/heatmap?path=${encodeURIComponent(path)}&viewport=1440&kind=CLICK`)
      .then(setData)
      .catch((err) => setError(err.message));
  }, [path]);

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg">Click heatmap</h2>
        <Select value={path} onChange={(e) => setPath(e.target.value)} className="w-56">
          {paths.map((row) => (
            <option key={row.path} value={row.path}>
              {row.path} ({row.points})
            </option>
          ))}
        </Select>
      </div>

      <ErrorNote error={error} />

      {!data ? (
        <Spinner label="Loading points…" />
      ) : (
        <>
          <p className="mt-3 text-xs text-muted">
            {data.count} clicks at {data.viewportBucket}px. Position is relative to the full
            page, top to bottom.
          </p>
          {/* Constrained width so the page-shaped 3:4 box does not become a
              full-width column of empty space on a wide screen. */}
          <div
            className="relative mt-4 w-full max-w-sm border-3 border-ink bg-paper"
            style={{ aspectRatio: '3 / 4' }}
          >
            {data.points.map((point, index) => (
              <span
                key={index}
                className="absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  left: `${point.x * 100}%`,
                  top: `${point.y * 100}%`,
                  // Additive blending makes overlapping clicks read as hot spots.
                  background:
                    'radial-gradient(circle, rgba(239,114,153,0.85) 0%, rgba(248,246,172,0.35) 60%, transparent 70%)',
                  mixBlendMode: 'multiply',
                }}
              />
            ))}
            {data.count === 0 && (
              <p className="absolute inset-0 flex items-center justify-center text-sm text-muted">
                No clicks recorded for this page yet.
              </p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    setData(null);
    api
      .get(`/api/admin/analytics/summary?days=${days}`)
      .then(setData)
      .catch((err) => setError(err.message));
  }

  useEffect(load, [days]);

  const eventRows = useMemo(
    () =>
      Object.entries(data?.eventsByType ?? {})
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count),
    [data],
  );

  const deviceRows = useMemo(
    () =>
      Object.entries(data?.devices ?? {})
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count),
    [data],
  );

  return (
    <>
      <PageTitle
        title="Analytics"
        description="First-party, measured on our own infrastructure. No third-party script means ad blockers do not distort these numbers."
        action={
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-40">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </Select>
        }
      />

      <ErrorNote error={error} onRetry={load} />

      {!data ? (
        <Spinner />
      ) : data.totals.sessions === 0 ? (
        <EmptyState
          title="No traffic recorded yet"
          description="Visits appear here once someone accepts the cookie banner. Nothing is tracked before consent is given."
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Sessions" value={compactNumber(data.totals.sessions)} />
            <Stat label="Unique visitors" value={compactNumber(data.totals.uniqueVisitors)} />
            <Stat label="Pageviews" value={compactNumber(data.totals.pageviews)} />
            <Stat
              label="Bounce rate"
              value={percent(data.totals.bounceRate, 1, 0)}
              sub="Single-page sessions"
            />
            <Stat
              label="Avg. duration"
              value={formatDuration(data.totals.avgDurationMs)}
              sub="Per session"
            />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <BarList
              title="Top pages"
              rows={data.topPages}
              labelKey="path"
              valueKey="views"
              empty="No pageviews yet."
            />
            <BarList
              title="Traffic sources"
              rows={data.sources}
              labelKey="source"
              valueKey="sessions"
              empty="No sessions yet."
            />
            <BarList
              title="Events"
              rows={eventRows}
              labelKey="type"
              valueKey="count"
              empty="No events yet."
            />
            <BarList
              title="Devices"
              rows={deviceRows}
              labelKey="device"
              valueKey="count"
              empty="No sessions yet."
            />
          </div>

          {data.heatmapPaths.length > 0 && (
            <div className="mt-8">
              <Heatmap paths={data.heatmapPaths} />
            </div>
          )}

          <Card className="mt-8 p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">
              Ingest buffer
            </p>
            <p className="mt-2 text-sm">
              {compactNumber(data.buffer.written)} rows written ·{' '}
              {data.buffer.queuedEvents + data.buffer.queuedHeatmapPoints} queued ·{' '}
              {data.buffer.dropped} dropped
            </p>
            <p className="mt-1 text-xs text-muted">
              Events are buffered in memory and flushed in batches, so tracking never adds a
              database round trip to a visitor&apos;s request.
            </p>
          </Card>
        </>
      )}
    </>
  );
}
